import { graphql } from 'gatsby';
import * as React from 'react';
import ImageSlider from '~/components/Modules/ImageSlider';
import { ConstructFragmentOverride, GetCopyFromJson } from '~/contentstack/rendering';
import GatsbyBackgroundImage from './images/GatsbyBackgroundImage';

import './Carousel.scss';

export const query = graphql`
	fragment CarouselFragment on Ozk_pageContent {
		carousel {
			carousel_name
			display_time
			transition_time
			height
			overlay_color

			pages {
				heading {
					json
				}
				subheading {
					json
				}
				background_image {
					...BynderImageFragment
				}
			}
		}
	}
`;
export const Carousel_REFERENCES = ['carousel.pages.background_image'];

/**
 * @typedef CarouselPage
 * @property {import('~/templates/CSTypes').CSJRTE} heading The main heading.
 * @property {import('~/templates/CSTypes').CSJRTE} subheading The second level heading.
 * @property {import('~/templates/CSTypes').BynderImageOptions} background_image The image to use as the background for the given page.
 *
 * @typedef CarouselOptions
 * @property {string} carousel_name The label for the carousel.
 * @property {number} display_time The display time per page in milliseconds.
 * @property {number} transition_time The time to transition between pages in milliseconds.
 * @property {number} height The desired height for the carousel.
 * @property {string} overlay_color The color to use as an overlay.
 * @property {CarouselPage[]} pages The pages to put into the carousel.
 *
 * @typedef {{ options: import('~/contentstack/types').ModelWithLivePreviewAttr<CarouselOptions> }} CarouselProps
 */
/** @type {import('~/types/React').ReactFC<CarouselProps>} */
export const Carousel = ({ options }) => {
	if (!options) {
		return null;
	}

	return (
		<ImageSlider
			className="cs-carousel container"
			autoPlay={true}
			interval={options.display_time}
			transitionTime={options.transition_time}
			infiniteLoop={true}
			showThumbs={false}
		>
			{options.pages.map((cp, i) => {
				const heading = GetCopyFromJson(cp.heading);
				const subheading = GetCopyFromJson(cp.subheading, ConstructFragmentOverride('p'));

				return (
					<GatsbyBackgroundImage className="carousel-item" key={i} option={cp.background_image?.[0]}>
						<div
							className="carousel-item-container"
							style={{ backgroundColor: options.overlay_color, height: options.height }}
						>
							<h1 className="heading" {...options.$?.heading}>
								{heading}
							</h1>
							<p className="subheading" {...options.$?.subheading}>
								{subheading}
							</p>
						</div>
					</GatsbyBackgroundImage>
				);
			})}
		</ImageSlider>
	);
};
